import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { eventsApi, Event } from '../api/client';
import { mediaApi } from '../api/mediaGenerator';
import { ArrowLeftIcon, ArrowDownTrayIcon, PhotoIcon } from '@heroicons/react/24/outline';
import { getErrorMessage } from '../utils/errorHandler';

interface MediaSettings {
  mediaType: 'countdown' | 'announcement' | 'reminder' | 'thank_you';
  countdownDays?: number;
  countdownText?: string;
  subtitleText: string;
  customMessage?: string;
  includeVenue: boolean;
  includeDate: boolean;
  includeLogo: boolean;
  colorTheme: 'indigo' | 'purple' | 'blue' | 'green' | 'red';
}

const MediaGenerator: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [event, setEvent] = useState<Event | null>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [generatedImage, setGeneratedImage] = useState<string | null>(null);
  
  const [settings, setSettings] = useState<MediaSettings>({
    mediaType: 'countdown',
    countdownDays: 14,
    subtitleText: "Don't miss out! Register now",
    includeVenue: true,
    includeDate: true,
    includeLogo: true,
    colorTheme: 'indigo'
  });

  // Predefined options
  const mediaTypes = [
    { value: 'countdown', label: 'Countdown', icon: '⏰' },
    { value: 'announcement', label: 'Announcement', icon: '📢' },
    { value: 'reminder', label: 'Reminder', icon: '🔔' },
    { value: 'thank_you', label: 'Thank You', icon: '🙏' }
  ];

  const countdownOptions = [
    { days: 30, text: '1 MONTH TO GO' },
    { days: 14, text: '2 WEEKS TO GO' },
    { days: 7, text: '1 WEEK TO GO' },
    { days: 3, text: '3 DAYS TO GO' },
    { days: 1, text: 'TOMORROW' },
    { days: 0, text: 'TODAY' }
  ];

  const subtitleTemplates = {
    countdown: [
      "Don't miss out! Register now",
      "Limited spots remaining!",
      "Secure your spot today",
      "Join us for an amazing event"
    ],
    announcement: [
      "Registration now open!",
      "Save the date!",
      "We're excited to announce",
      "Mark your calendars"
    ],
    reminder: [
      "Final spots available!",
      "Last chance to register",
      "Don't forget to register",
      "Registration closing soon"
    ],
    thank_you: [
      "Thanks for joining us!",
      "It was great to see you",
      "Thank you for attending",
      "See you next time!"
    ]
  };

  const colorThemes = [
    { value: 'indigo', label: 'Indigo', color: 'bg-indigo-600' },
    { value: 'purple', label: 'Purple', color: 'bg-purple-600' },
    { value: 'blue', label: 'Blue', color: 'bg-blue-600' },
    { value: 'green', label: 'Green', color: 'bg-green-600' },
    { value: 'red', label: 'Red', color: 'bg-red-600' }
  ];

  useEffect(() => {
    if (id) {
      loadEvent(parseInt(id));
    }
  }, [id]);

  useEffect(() => {
    // Auto-generate when settings change
    if (event && !loading) {
      generateMedia();
    }
  }, [settings]);

  const loadEvent = async (eventId: number) => {
    try {
      const data = await eventsApi.getEvent(eventId);
      setEvent(data);
    } catch (error) {
      alert(getErrorMessage(error));
      navigate('/organizers');
    } finally {
      setLoading(false);
    }
  };

  const generateMedia = async () => {
    if (!event) return;
    
    setGenerating(true);
    try {
      const response = await mediaApi.generateMedia(event.id, {
        media_type: settings.mediaType,
        countdown_days: settings.countdownDays,
        countdown_text: settings.countdownText,
        subtitle_text: settings.subtitleText,
        custom_message: settings.customMessage,
        include_venue: settings.includeVenue,
        include_date: settings.includeDate,
        include_logo: settings.includeLogo,
        color_theme: settings.colorTheme
      });
      
      setGeneratedImage(response.media_image);
    } catch (error) {
      console.error('Error generating media:', error);
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = () => {
    if (generatedImage) {
      const link = document.createElement('a');
      link.href = generatedImage;
      link.download = `${settings.mediaType}-${event?.title.toLowerCase().replace(/\s+/g, '-')}-${Date.now()}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleCountdownChange = (days: number, text: string) => {
    setSettings({
      ...settings,
      countdownDays: days,
      countdownText: text
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!event) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/organizers')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeftIcon className="h-5 w-5" />
            Back to Events
          </button>
          
          <div className="flex items-center gap-3">
            <PhotoIcon className="h-8 w-8 text-indigo-600" />
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Create Media</h1>
              <p className="text-gray-600 mt-1">Generate promotional graphics for {event.title}</p>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Settings Panel */}
          <div className="bg-white rounded-lg shadow-lg p-6 space-y-6">
            <h2 className="text-xl font-semibold">Media Settings</h2>

            {/* Media Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Media Type
              </label>
              <div className="grid grid-cols-2 gap-3">
                {mediaTypes.map(type => (
                  <button
                    key={type.value}
                    onClick={() => setSettings({ 
                      ...settings, 
                      mediaType: type.value as any,
                      subtitleText: subtitleTemplates[type.value as keyof typeof subtitleTemplates][0]
                    })}
                    className={`p-3 rounded-lg border-2 transition-colors ${
                      settings.mediaType === type.value
                        ? 'border-indigo-600 bg-indigo-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="text-2xl mb-1">{type.icon}</div>
                    <div className="text-sm font-medium">{type.label}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Countdown Options (for countdown type) */}
            {settings.mediaType === 'countdown' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Countdown Period
                </label>
                <select
                  value={settings.countdownDays}
                  onChange={(e) => {
                    const option = countdownOptions.find(o => o.days === parseInt(e.target.value));
                    if (option) {
                      handleCountdownChange(option.days, option.text);
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  {countdownOptions.map(option => (
                    <option key={option.days} value={option.days}>
                      {option.text}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Subtitle Text */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Subtitle Text
              </label>
              <select
                value={settings.subtitleText}
                onChange={(e) => setSettings({ ...settings, subtitleText: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 mb-2"
              >
                {subtitleTemplates[settings.mediaType].map(template => (
                  <option key={template} value={template}>
                    {template}
                  </option>
                ))}
              </select>
              <input
                type="text"
                value={settings.subtitleText}
                onChange={(e) => setSettings({ ...settings, subtitleText: e.target.value })}
                placeholder="Or enter custom text..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            {/* Color Theme */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Color Theme
              </label>
              <div className="flex gap-2">
                {colorThemes.map(theme => (
                  <button
                    key={theme.value}
                    onClick={() => setSettings({ ...settings, colorTheme: theme.value as any })}
                    className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                      settings.colorTheme === theme.value
                        ? `${theme.color} text-white`
                        : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                    }`}
                  >
                    {theme.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Display Options */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Display Options
              </label>
              <div className="space-y-2">
                {settings.mediaType !== 'thank_you' && (
                  <>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={settings.includeDate}
                        onChange={(e) => setSettings({ ...settings, includeDate: e.target.checked })}
                        className="rounded text-indigo-600 mr-2"
                      />
                      <span className="text-sm">Include event date</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={settings.includeVenue}
                        onChange={(e) => setSettings({ ...settings, includeVenue: e.target.checked })}
                        className="rounded text-indigo-600 mr-2"
                      />
                      <span className="text-sm">Include venue location</span>
                    </label>
                  </>
                )}
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={settings.includeLogo}
                    onChange={(e) => setSettings({ ...settings, includeLogo: e.target.checked })}
                    className="rounded text-indigo-600 mr-2"
                  />
                  <span className="text-sm">Include company logo</span>
                </label>
              </div>
            </div>

            {/* Tips */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="font-medium text-blue-900 mb-2">Tips for sharing:</h3>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• Use countdown cards 2-4 weeks before the event</li>
                <li>• Share on LinkedIn, Twitter, and Slack</li>
                <li>• Include relevant hashtags for better reach</li>
                <li>• Tag @star.london to increase visibility</li>
              </ul>
            </div>
          </div>

          {/* Preview Panel */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-xl font-semibold mb-4">Preview</h2>
            
            <div className="relative bg-gray-100 rounded-lg overflow-hidden aspect-[1200/630] mb-4">
              {generatedImage ? (
                <img
                  src={generatedImage}
                  alt="Generated media preview"
                  className="w-full h-full object-contain"
                />
              ) : (
                <div className="flex items-center justify-center h-full">
                  {generating ? (
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
                      <p className="text-gray-500">Generating media...</p>
                    </div>
                  ) : (
                    <p className="text-gray-400">Preview will appear here</p>
                  )}
                </div>
              )}
            </div>

            {generatedImage && (
              <button
                onClick={handleDownload}
                className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium transition-colors"
              >
                <ArrowDownTrayIcon className="h-5 w-5" />
                Download Media
              </button>
            )}

            {/* Size info */}
            <p className="text-xs text-gray-500 text-center mt-4">
              Image size: 1200 × 630px (Optimized for social media)
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MediaGenerator;